/**
 * Investigation template list (Module 24) — `GET /investigations/templates`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference): the grid
 * owns paging, search and filtering, so the page keeps only the column plan, the row actions, one
 * `DeleteDialog`, and the single piece of refetch state (`listVersion`).
 *
 * Sibling route note: `InvestigationListPage` lives at `ROUTES.INVESTIGATIONS` and is a separate
 * page. This one is the templates list — heading "Investigation Templates", create target
 * `ROUTES.INVESTIGATION_TEMPLATE_NEW`.
 *
 * The endpoint returns a bare `InvestigationTemplateDto[]` with a single `activeOnly` boolean and
 * no paging or search, so the `dataSource` below fetches the whole list and does the search, the
 * inactive-only narrowing and the page slice locally before handing
 * `createCortexPagedDataSource` a `PagedResult`-shaped object. That keeps the grid's toolbar
 * working and keeps the offset→page arithmetic in one tested place.
 *
 * ── Column plan ──────────────────────────────────────────────────────────────────────────────────
 *  field              | header  | width | cell type                 | filter
 *  -------------------|---------|-------|---------------------------|-----------------------------
 *  name               | Name    |  30%  | text (clickable)          | toolbar search (local)
 *  description        | Purpose |  30%  | text, maxLength 90 + more  | toolbar search (local)
 *  isActive           | Status  |  14%  | custom → Chip             | status tabs (→ activeOnly)
 *  isSystemTemplate   | Origin  |  12%  | custom → Chip             | —
 *  updatedAt          | Updated |  14%  | date (locale)             | —
 *
 * Dropped: `stepsJson` and `defaultPromptsJson` (raw JSON — the template's substance, but it
 * belongs on the edit form, not in a list cell), `createdAt` (`updatedAt` is the one that moves),
 * `tenantId` / `projectId`.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Trash2 } from 'lucide-react';

import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { investigationService } from '../services/investigationService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import type { InvestigationTemplateDto } from '../types/investigation';
import type { PagedResult } from '../types/tenant';

import './InvestigationTemplatesListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to `paramKey`, and it builds each tab's test id
 * as `cordio-datatable-status-tab-<value>`. GRID-13 asserts on `-all` / `-true` / `-false`, so the
 * All tab must carry the literal `'all'` — an empty value would emit
 * `cordio-datatable-status-tab-` and the checklist would select nothing.
 *
 * `paramKey` is the synthetic `status` because the endpoint's only status control is a single
 * `activeOnly` boolean, which has no "inactive only" setting at all: Inactive is therefore
 * `activeOnly: false` plus a local narrowing. See {@link readStatusTab}.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Splits the synthetic tab value into what the endpoint can do and what only the page can do.
 *
 * `activeOnly: true` is the only server-side narrowing available; `activeOnly: false` returns both
 * active and inactive templates, so Inactive has to be finished off locally.
 */
function readStatusTab(value: unknown): { activeOnly: boolean; inactiveOnly: boolean } {
  if (value === 'true') return { activeOnly: true, inactiveOnly: false };
  if (value === 'false') return { activeOnly: false, inactiveOnly: true };
  // Absent or 'all'.
  return { activeOnly: false, inactiveOnly: false };
}

/** Free-text match over the two fields the row shows as text — the same pair as before migration. */
function matchesSearch(row: InvestigationTemplateDto, term: string): boolean {
  return `${row.name} ${row.description ?? ''}`.toLowerCase().includes(term);
}

const templateEditPath = (id: string) => ROUTES.INVESTIGATION_TEMPLATE_EDIT.replace(':id', id);

export const InvestigationTemplatesListPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<InvestigationTemplateDto>();

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<InvestigationTemplateDto>(async (query) => {
        const { activeOnly, inactiveOnly } = readStatusTab(query[STATUS_TAB_PARAM_KEY]);
        const all = await investigationService.getInvestigationTemplates(activeOnly);

        const term = typeof query.searchTerm === 'string' ? query.searchTerm.trim().toLowerCase() : '';
        let matched = inactiveOnly ? all.filter((row) => !row.isActive) : all;
        if (term) matched = matched.filter((row) => matchesSearch(row, term));

        // The endpoint has no paging, so the page window is cut here. `pageNumber` is 1-based —
        // createCortexPagedDataSource already turned the grid's SkipCount into it.
        const start = (query.pageNumber - 1) * query.pageSize;
        const items = matched.slice(start, start + query.pageSize);

        const page: PagedResult<InvestigationTemplateDto> = {
          items,
          totalCount: matched.length,
          pageNumber: query.pageNumber,
          pageSize: query.pageSize,
          totalPages: Math.max(1, Math.ceil(matched.length / query.pageSize)),
          hasPreviousPage: query.pageNumber > 1,
          hasNextPage: start + query.pageSize < matched.length,
        };
        return page;
      }),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<InvestigationTemplateDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '30%', clickable: true },
      {
        field: 'description',
        header: 'Purpose',
        type: 'text',
        width: '30%',
        maxLength: 90,
        showReadMore: true,
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '14%',
        // A Chip rather than the built-in boolean cell: "Active/Inactive" is the vocabulary the
        // rest of the product uses, and it matches the status tabs above the grid.
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
      {
        field: 'isSystemTemplate',
        header: 'Origin',
        type: 'custom',
        width: '12%',
        // Its own column rather than a second chip in Status: whether a template is shipped or
        // authored here decides whether it can be deleted, which is why it earns the width.
        customCell: (row) => (
          <Chip label={row.isSystemTemplate ? 'System' : 'Custom'} color={row.isSystemTemplate ? 'blue' : 'default'} />
        ),
      },
      { field: 'updatedAt', header: 'Updated', type: 'date', width: '14%', dateFormat: 'locale' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search templates by name or purpose' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Template status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<InvestigationTemplateDto>>>(
    () => [
      {
        icon: 'square-pen',
        tooltip: 'Edit template',
        onClick: (row) => navigate(templateEditPath(row.id)),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete template',
        className: 'investigation-templates-list__action--destructive',
        // System templates cannot be deleted. `visible` rather than a disabled button plus a
        // warning toast: an action that can never succeed should not be offered at all.
        visible: (row) => !row.isSystemTemplate,
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await investigationService.deleteInvestigationTemplate(pendingItem.id);
    showNotification('Template deleted successfully', 'success');

    setListVersion((v) => v + 1);
  }, [pendingItem, showNotification]);

  return (
    <div className="investigation-templates-list" data-testid="investigation-template-list-page">
      <ShellHeader
        title="Investigation Templates"
        description="Reusable step plans and prompts a new incident investigation can start from."
        data-testid="investigation-template-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-investigation-template-btn"
            onClick={() => navigate(ROUTES.INVESTIGATION_TEMPLATE_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create Template
          </Button>
        }
      />

      <div data-testid="investigation-template-list-grid">
        <CordioAbpPagedDataTable<InvestigationTemplateDto>
          className="investigation-templates-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(templateEditPath(row.id))}
          onCellClick={({ row }) => navigate(templateEditPath(row.id))}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete template?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="Investigations already created from this template keep their steps. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
